// A button that opens a small popover menu (same surface as the profile menu). The menu is position: fixed, placed from the
// trigger's rect, so a scrolling table wrapper can't clip it.

import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useCloseWhenMoved } from './closeWhenMoved';

export type MenuItem = { key: string; label: string; icon?: ReactNode; danger?: boolean; testid?: string; onSelect: () => void };

/** `ariaLabel` names a trigger that is only an icon (⋯). */
export function ActionMenu({ label, items, className = '', testid, ariaLabel }: { label: ReactNode; items: MenuItem[]; className?: string; testid?: string; ariaLabel?: string }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();

  // below the trigger, or above it when there is no room
  useLayoutEffect(() => {
    if (!open || !pos || !menu.current || !trigger.current) return;
    const h = menu.current.getBoundingClientRect().height;
    const r = trigger.current.getBoundingClientRect();
    const flip = pos.top + h > window.innerHeight - 8 && r.top - h - 8 > 8;
    const top = flip ? r.top - h - 8 : pos.top;
    if (Math.abs(top - pos.top) > 0.5) setPos({ ...pos, top });
  }, [open, pos]);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = trigger.current?.getBoundingClientRect();
      if (r) setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
    };
    place();
    const els = () => Array.from(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    els()[0]?.focus();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!menu.current?.contains(t) && !trigger.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const list = els();
        const i = list.indexOf(document.activeElement as HTMLElement);
        list[(i + (e.key === 'ArrowDown' ? 1 : -1) + list.length) % list.length]?.focus();
      } else if (e.key === 'Tab') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  useCloseWhenMoved(open, trigger, () => setOpen(false));

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={className}
        aria-haspopup="menu"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((o) => !o)}
        data-testid={testid}
      >
        {label}
      </button>
      {open && pos && (
        <div ref={menu} id={id} role="menu" className="profile-pop action-pop" style={{ position: 'fixed', top: pos.top, right: pos.right }}>
          <div className="profile-pop-list">
            {items.map((it, i) => (
              <div key={it.key}>
                {it.danger && i > 0 && <hr className="profile-sep" />}
                <button
                  type="button"
                  role="menuitem"
                  className={`profile-item${it.danger ? ' is-danger' : ''}`}
                  data-testid={it.testid}
                  onClick={() => {
                    setOpen(false);
                    it.onSelect();
                  }}
                >
                  {it.icon}
                  {it.label}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
